import { ErrorMessage } from "@/components/UI";
import { Button } from "@/components/UI/Buttons/Button";
import medicalReportProtocolService, {
  ClarifyResponse,
} from "@/services/medicalReportProtocolService";
import { useState, useEffect } from "react";

interface ClarificationFormProps {
  promptKey: string;
  originalAnswer: string;
  promptContent: string;
  uploadId: string;
  conversationId: string;
  onSelectionChange?: (
    key: string,
    answer: string,
    isClarified: boolean
  ) => void;
}

const ClarificationForm = ({
  promptKey,
  promptContent,
  originalAnswer,
  uploadId,
  conversationId,
  onSelectionChange,
}: ClarificationFormProps) => {
  const [clarificationText, setClarificationText] = useState("");
  const [clarificationAnswer, setClarificationAnswer] = useState<string | null>(
    null
  );
  const [isClarifying, setIsClarifying] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>();
  const [selectedAnswer, setSelectedAnswer] = useState<
    "original" | "clarified"
  >("original");

  useEffect(() => {
    const isClarified = selectedAnswer === "clarified";
    const answer = isClarified ? clarificationAnswer || "" : originalAnswer;

    onSelectionChange?.(promptKey, answer, isClarified);
  }, [selectedAnswer, clarificationAnswer, promptKey, originalAnswer]);

  const handleClarify = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsClarifying(true);
    setErrorMsg(undefined);

    try {
      const response: ClarifyResponse =
        await medicalReportProtocolService.clarify({
          upload_id: uploadId,
          conversation_id: conversationId,
          prompt_content: clarificationText,
          previous_answer: originalAnswer,
        });

      setClarificationAnswer(response.answer);
    } catch (error) {
      console.error(error);
      setErrorMsg(`CLARIFY FAILED: ${error}`);
    } finally {
      setIsClarifying(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-4 p-4 border border-gray-300 rounded-md"
      onSubmit={handleClarify}
    >
      <div>
        <p className="block text-sm font-medium mb-1">Prompt</p>
        <div className="p-2 bg-gray-100 rounded border">{promptContent}</div>
      </div>

      <label
        htmlFor={`original-${promptKey}`}
        className="flex items-start gap-3 cursor-pointer"
      >
        <input
          type="radio"
          id={`original-${promptKey}`}
          name={`answer-original-${promptKey}`}
          checked={selectedAnswer === "original"}
          onChange={() => setSelectedAnswer("original")}
          className="mt-1"
        />
        <div className="flex-1">
          <div className="text-sm font-medium mb-1">Answer</div>
          <div className="p-2 bg-gray-100 rounded border">{originalAnswer}</div>
        </div>
      </label>

      <div>
        <label className="block text-sm font-medium mb-1">
          Clarification text - optional
        </label>
        <textarea
          value={clarificationText}
          onChange={(e) => setClarificationText(e.target.value)}
          placeholder="Enter any additional clarification..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={4}
        />
      </div>

      {clarificationAnswer && (
        <label
          htmlFor={`clarified-${promptKey}`}
          className="flex items-start gap-3 cursor-pointer"
        >
          <input
            type="radio"
            id={`clarified-${promptKey}`}
            name={`answer-clarified-${promptKey}`}
            checked={selectedAnswer === "clarified"}
            onChange={() => setSelectedAnswer("clarified")}
            className="mt-1"
          />
          <div className="flex-1">
            <div className="text-sm font-medium mb-1">Clarification answer</div>
            <div className="p-2 bg-gray-100 rounded border">
              {clarificationAnswer}
            </div>
          </div>
        </label>
      )}

      <Button
        type="submit"
        disabled={isClarifying || !clarificationText.trim()}
        size="sm"
      >
        {isClarifying ? "Clarifying..." : "Clarify"}
      </Button>

      <ErrorMessage error={errorMsg} />
    </form>
  );
};

export default ClarificationForm;
